import { GrowthTypes } from '@ecosystem/formatters';

export type StackChartDataType = Array<{
  segment?: string;
  share?: {
    series?: Array<{
      label: string;
      data: Array<{ label: string; value: number }>;
      color?: { background: string; color: string };
    }>;
    data?: Array<{ label?: string; value?: number; percentage?: number }>;
  };
  data?: Array<{ value?: number; percentage?: number; actual?: number }>;
}>;

export type KpiData = {
  name: string;
  data: (number | null)[];
  color?: { background: string; color: string };
};

export type ExploreTrendsStackKpiDataDto = {
  kpiType: string | undefined;
  kpiData: KpiData[];
};

export class ExportExploreTrendsStack {
  salesRecordsFound = false;
  categories: string[] = [];
  revenueChangeInPercent: (number | null)[] = [];
  revenueChangeInAbsolute: (number | null)[] = [];
  totalRevenueInMillion: (number | null)[] = [];
  segmentShare: (number | null)[] = [];
  shareSegmentNames: string[] = [];
  categorySegments: string[] = [];
  series: ExploreTrendsStackKpiDataDto[] = [];
}

const toNumOrNull = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

type Unit = '' | 'k' | 'M';

export const convertToKOrM = (v: unknown): { value: number; unit: Unit } | null => {
  const n = toNumOrNull(v);
  if (n == null) return null;

  const abs = Math.abs(n);
  if (abs < 100_000) return { value: n, unit: '' };

  const r1 = (x: number) => Math.round(x * 10) / 10;

  if (abs < 1_000_000) {
    const val = r1(n / 1_000);
    if (Math.abs(val) >= 1000) return { value: r1(n / 1_000_000), unit: 'M' };
    return { value: val, unit: 'k' };
  }

  return { value: r1(n / 1_000_000), unit: 'M' };
};

const decodeEntities = (s: string) => s.replaceAll('&amp;', '&');
const uniq = <T>(arr: T[]) => [...new Set(arr)];
const mkArr = <T>(len: number, fill: T) => Array.from({ length: len }, () => fill);

type Options = {
  StackChartData: StackChartDataType;
  selectedKpi: string | undefined;
  selectedGrowthType: GrowthTypes;
  fillMissing?: number | null;
  decodeHtmlEntities?: boolean;
};

export const convertToExploreTrendsStack = ({
  StackChartData,
  selectedKpi,
  selectedGrowthType,
  fillMissing = null,
  decodeHtmlEntities = true,
}: Options): ExportExploreTrendsStack => {
  const out = new ExportExploreTrendsStack();

  const segments = Array.isArray(StackChartData) ? StackChartData : [];
  if (!segments.length) return out;

  const segmentBlocks = segments.map((seg) => {
    const segmentName = seg?.segment === 'NO' ? 'OTHERS' : (seg?.segment ?? 'Unknown');
    const series = seg?.share?.series ?? [];
    const shareTotals = seg?.share?.data ?? [];

    const monthsFromSeries = uniq(series.flatMap((s) => s?.data?.map((d) => d.label) ?? []));
    const monthsFromShare = uniq(shareTotals.map((d) => d?.label ?? '').filter(Boolean));
    const months = uniq([...monthsFromSeries, ...monthsFromShare]);

    return {
      segmentName,
      months,
      series,
      shareTotals,
      topData: seg?.data ?? [],
    };
  });

  const categories = segmentBlocks.flatMap((b) => b.months);
  const categorySegments = segmentBlocks.flatMap((b) => mkArr(b.months.length, b.segmentName));

  out.categories = categories;
  out.categorySegments = categorySegments;
  out.shareSegmentNames = categorySegments.slice();

  const N = categories.length;
  const nulls = mkArr<number | null>(N, null);

  out.totalRevenueInMillion = nulls.slice();
  out.revenueChangeInPercent = nulls.slice();
  out.segmentShare = nulls.slice();

  const allSeriesItems = segmentBlocks.flatMap((b) => b.series ?? []);
  const stackNames = uniq(allSeriesItems.map((s) => (decodeHtmlEntities ? decodeEntities(s.label) : s.label)));

  const seriesMap = new Map<
    string,
    { name: string; data: (number | null)[]; color?: { background: string; color: string } }
  >(stackNames.map((name) => [name, { name, data: mkArr(N, fillMissing) }]));

  for (const s of allSeriesItems) {
    const name = decodeHtmlEntities ? decodeEntities(s.label) : s.label;
    const entry = seriesMap.get(name)!;
    if (!entry.color && s.color) entry.color = s.color;
  }

  let offset = 0;
  for (const block of segmentBlocks) {
    const { months, series, shareTotals, topData } = block;
    const count = months.length;

    const shareByLabel = Object.fromEntries(
      shareTotals.filter((d) => d?.label != null).map((d) => [String(d.label), toNumOrNull(d.value)]),
    );

    months.forEach((m, i) => {
      const idx = offset + i;
      out.segmentShare[idx] = (m in shareByLabel ? shareByLabel[m] : toNumOrNull(shareTotals[i]?.value)) ?? null;
      out.revenueChangeInPercent[idx] = topData[i][selectedGrowthType];
      out.revenueChangeInAbsolute[idx] = convertToKOrM(topData[i][selectedGrowthType]);
      out.totalRevenueInMillion[idx] = convertToKOrM(topData[i]?.value);
    });

    for (const s of series) {
      const name = decodeHtmlEntities ? decodeEntities(s.label) : s.label;
      const valueMap = Object.fromEntries((s.data ?? []).map((d) => [d.label, toNumOrNull(d.value)]));
      const target = seriesMap.get(name)!;
      months.forEach((m, i) => {
        const idx = offset + i;
        const v = valueMap[m];
        target.data[idx] = v ?? fillMissing;
      });
    }

    offset += count;
  }

  out.series = [{ kpiType: selectedKpi, kpiData: [...seriesMap.values()] }];
  out.salesRecordsFound = out.series[0]?.kpiData?.length > 0 && N > 0;
  return out;
};
